import * as React from 'react'

import { Box, SearchField, Skeleton } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import EmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters'
import type LogicOperatorForm from 'modules/core/components/MultiFilters/Forms/LogicOperatorForm'
import type MultiFilterForm from 'modules/core/components/MultiFilters/Forms/MultiFilterForm'
import type { FilterOption as MultiFilterOption } from 'modules/core/components/MultiFilters/types'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import filterFields from 'modules/payroll_bundle/components/Supplements/components/Table/_filters/filterFields'
import { usePayrollContextlessPolicies } from 'modules/payroll_bundle/hooks/usePayrollContextlessPolicies'
import { PayrollStatus } from 'modules/payroll_bundle/lib/enums'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import { useStatusAuthorizations } from 'modules/payroll_bundle/lib/statusAuthorizationsContext'
import supplementBulkImportCollection from 'modules/payroll_bundle/store/collections/supplementBulkImports'

import { BulkImportSplitButton } from './_components/BulkImportSplitButton'

interface Props {
  onSearch: (event: React.ChangeEvent<HTMLInputElement>) => void
  search: string
  filters: MultiFilterForm[]
  logicOperator: LogicOperatorForm
  onAddFilter: () => void
  onRemoveFilter: (index: number) => void
  onApplyFilters: () => void
  onCancel: () => void
}

const SearchBar = ({
  onSearch,
  search,
  filters,
  logicOperator,
  onAddFilter,
  onRemoveFilter,
  onApplyFilters,
  onCancel,
}: Props) => {
  const { currentPolicyPeriods, periodIndex } = usePolicyPeriods()

  const currentPolicyPeriodStatus = currentPolicyPeriods[periodIndex]?.get('status')

  const [fields, setFields] = React.useState<readonly MultiFilterOption[]>([])

  const { loading } = useAsyncData(async () => {
    const _fields = await filterFields()
    setFields(_fields)

    const data = {
      data: {
        policy_period_id: currentPolicyPeriods[periodIndex]?.id,
      },
    }
    await supplementBulkImportCollection.fetch(data)
  }, [])

  // Bulk supplement import support
  const allowedStatuses: PayrollStatus[] = [
    PayrollStatus.PREPARATION,
    PayrollStatus.SUPPLEMENTS_DEFINITION,
  ]
  const isEditableStatus = allowedStatuses.includes(currentPolicyPeriodStatus)
  const { canImportSupplements } = usePayrollContextlessPolicies()
  const val = useStatusAuthorizations()

  // If the policy period is in `preparation` supplements can be added by a user
  // with the ManagePayroll permission. Otherwise, even if the user is allowed
  // to import supplements, they can only do so during `supplement_definition` status.
  const canEditSupplementsInCurrentStatus = val.policyPeriodAuthorizations.edit_supplements
  const importDisabledReason = useMemo(() => {
    if (loading) {
      return i18n.t('common.please_wait')
    } else if (!isEditableStatus) {
      return i18n.t('payroll_bundle.import_supplements.import.disabled.invalid_status', {
        phase: i18n.t('payroll_bundle.policy_period.status.phases.supplements_definition'),
      })
    } else if (!canEditSupplementsInCurrentStatus) {
      return i18n.t('payroll_bundle.import_supplements.import.disabled.collaboration_off')
    } else {
      return null
    }
  }, [loading, isEditableStatus, canEditSupplementsInCurrentStatus])

  const importDisabled = !!importDisabledReason

  return (
    <>
      <Box flexNoShrink>
        {loading && <Skeleton width="s96" height="s32" />}
        {!loading && (
          <EmployeeMultiFilters
            label={i18n.t('filters.multi_filters.title')}
            fields={fields}
            filters={filters}
            logicOperator={logicOperator}
            onAddFilter={onAddFilter}
            onRemoveFilter={onRemoveFilter}
            onApplyFilters={onApplyFilters}
            onCancel={onCancel}
          />
        )}
      </Box>
      <Box flexGrow paddingLeft="s8">
        <SearchField
          variant="big"
          onSearch={onSearch}
          search={search}
          placeholder={i18n.t('common.search')}
        />
      </Box>
      {canImportSupplements && isEditableStatus && (
        <Box
          paddingLeft="s8"
          flexDirection="row"
          gap="s8"
          aria-label={i18n.t('payroll_bundle.accessibility.import_split_button')}
        >
          <BulkImportSplitButton
            tooltipMessage={importDisabledReason ?? undefined}
            disabled={importDisabled}
            didImportSupplements={supplementBulkImportCollection.length > 0}
          />
        </Box>
      )}
    </>
  )
}

export default observer(SearchBar)
